import * as itineraryRepo from "../repo/itineraryRepo";
import {
  derivePublicItinerary,
  inferPendingPublicRoutes,
} from "../service/itineraryOutputService";
import { isHttpClientError } from "../../../lib/http/errors";
import type { ItineraryEditorDocument, ItineraryReadonlyPreview } from "../types";

export type ItineraryPreviewLoadResult =
  | { mode: "editor"; document: ItineraryEditorDocument }
  | { mode: "readonly"; preview: ItineraryReadonlyPreview };

/** 统计公共路径及专业轨道中的全部展示线路。 */
function publicSegmentCount(itinerary: ItineraryReadonlyPreview["itinerary"]): number {
  return itinerary.days.reduce((count, day) => count
    + day.segments.length
    + (day.parallelTracks ?? []).reduce((trackCount, track) => trackCount + track.segments.length, 0), 0);
}

/** 优先读取严格编辑图，失败时降级为不可保存的历史事实预览。 */
async function loadPreview(itineraryId: string): Promise<ItineraryPreviewLoadResult> {
  try {
    return { mode: "editor", document: await itineraryRepo.getEditor(itineraryId) };
  } catch {
    const preview = await itineraryRepo.getReadonlyPreview(itineraryId);
    const itinerary = inferPendingPublicRoutes(preview.itinerary);
    const beforeCount = publicSegmentCount(preview.itinerary);
    const afterCount = publicSegmentCount(itinerary);
    return {
      mode: "readonly",
      preview: {
        ...preview,
        itinerary,
        warnings: afterCount > beforeCount
          ? [...preview.warnings, "历史数据未保存交通段，已按点位顺序补充默认步行线路。"]
          : preview.warnings,
      },
    };
  }
}

/** 独立预览页使用的稳定运行时门面。 */
export const itineraryPreviewRuntime = {
  getEditor: itineraryRepo.getPreviewEditor,
  getReadonlyPreview: (itineraryId: string) => itineraryRepo.getReadonlyPreview(itineraryId),
  loadPreview,
  finalize: itineraryRepo.finalizeItinerary,
  unfinalize: itineraryRepo.unfinalizeItinerary,
  setReview: (itineraryId: string, revision: string, isReviewing: boolean) =>
    itineraryRepo.setItineraryReview(itineraryId, revision, isReviewing),
  createShareLink: itineraryRepo.createShareLink,
  deriveOutput: derivePublicItinerary,
};

/** 把预览加载错误转换为明确且可执行的中文提示。 */
export function itineraryPreviewLoadErrorText(error: unknown): string {
  if (isHttpClientError(error)) {
    if (error.code === "ITINERARY_REVIEW_REQUIRED") {
      return "行程尚未开放审稿，请等待计调完成后开启审稿。";
    }
    if (error.statusCode === 404 || error.code === "ITINERARY_NOT_FOUND") {
      return "行程不存在或已删除，请关闭后重新选择。";
    }
    if (error.statusCode === 403) {
      return "你没有查看该行程的权限。";
    }
    if (error.code === "ITINERARY_INVALID_GRAPH" || error.code === "ITINERARY_REFERENCE_INVALID") {
      return "行程历史数据暂无法安全预览，请联系管理员处理。";
    }
  }
  return "行程详情加载失败，请稍后重试。";
}

/** 仅为网络抖动、限流和服务端异常等可恢复失败提供重试。 */
export function canRetryItineraryPreviewLoad(error: unknown): boolean {
  if (!isHttpClientError(error)) return true;
  if (
    error.statusCode === 403
    || error.statusCode === 404
    || error.code === "ITINERARY_NOT_FOUND"
    || error.code === "ITINERARY_INVALID_GRAPH"
    || error.code === "ITINERARY_REFERENCE_INVALID"
  ) {
    return false;
  }
  return error.retriable;
}

/** 把定稿状态迁移错误转换为可执行的中文恢复提示。 */
export function itineraryStatusMutationErrorText(
  error: unknown,
  action: "finalize" | "unfinalize" | "review" | "unreview" = "finalize",
): string {
  if (isHttpClientError(error)) {
    if (error.code === "ITINERARY_REVISION_CONFLICT") {
      if (action === "review" || action === "unreview") {
        return "服务器版本已更新，请重新加载后再操作审稿状态。";
      }
      return action === "unfinalize"
        ? "服务端版本已更新，请重新加载后再确认取消定稿。"
        : "服务端版本已更新，请重新加载后再确认定稿。";
    }
    if (error.code === "ITINERARY_INVALID_GRAPH" || error.code === "ITINERARY_REFERENCE_INVALID") {
      return action === "unfinalize"
        ? "该行程暂时无法取消定稿，请联系管理员修复数据。"
        : "行程内容尚不完整，请先返回编辑页修正后再定稿。";
    }
    if (error.code === "ITINERARY_STATUS_CONFLICT" || error.code === "ITINERARY_READONLY") {
      return "行程状态已变化，请重新加载后再操作。";
    }
    if (error.code === "ITINERARY_REVIEW_REQUIRED") {
      return "行程尚未开放审稿，请等待计调完成后开启审稿。";
    }
    if (error.statusCode === null) {
      return "请求响应中断，无法确认操作结果，请重新加载最新状态。";
    }
  }
  return "操作失败，请稍后重试。";
}

/** 网络中断或客户端超时无法证明服务端事务是否已提交，必须读取最新状态核验。 */
export function isItineraryStatusMutationOutcomeUnknown(error: unknown): boolean {
  return !isHttpClientError(error) || error.statusCode === null;
}

/** 仅状态或 revision 已变化时允许从当前抽屉直接刷新最新快照。 */
export function canReloadAfterItineraryStatusMutation(error: unknown): boolean {
  return isItineraryStatusMutationOutcomeUnknown(error) || (isHttpClientError(error) && [
    "ITINERARY_REVISION_CONFLICT",
    "ITINERARY_STATUS_CONFLICT",
    "ITINERARY_READONLY",
  ].includes(error.code ?? ""));
}
